import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import MetricInput from '../components/MetricInput';

import ControlledDraftJson, { resourceEditContent } from '../components/ControlledDraftJson';
import ProcedureAuthoringMetadata from '../components/ProcedureAuthoringMetadata';
import ProcedureRevisionBar from '../components/ProcedureRevisionBar';

const API_BASE = process.env.REACT_APP_API_URL;
const EQUIPMENT_TYPES = ['Test Bench','Measurement Instrument','Programming Station','Computer / Workstation',
  'Soldering Station','Label Printer','Barcode Scanner','Camera / Vision System','Power Supply',
  'Climate Chamber','Conveyor / Transport','Hand Tool','Generic'];
const SUPPLIERS = ['gpv','ubiqconn','acmepoint','hk_ringer'];
const STATUS_COLORS = { Draft:'#94a3b8','In Review':'#60a5fa',Released:'#46BB89',Superseded:'#f59e0b',Obsolete:'#EC2124' };
const TRANSITIONS = { submit:{from:'Draft',to:'In Review',label:'Submit for Review'},revise:{from:'Released',to:'Draft',label:'Revise / New Draft'},approve:{from:'In Review',to:'Released',label:'Approve & Release'},reject:{from:'In Review',to:'Draft',label:'Reject'},obsolete:{from:null,to:'Obsolete',label:'Mark Obsolete'} };

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}
function StatusBadge({ status }) {
  const c = STATUS_COLORS[status]||'#666';
  return <span style={{ display:'inline-block',padding:'2px 10px',borderRadius:20,fontSize:11,
    fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',background:c+'22',color:c }}>{status}</span>;
}

export default function EquipmentDetail() {
  const { id } = useParams(); const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedRevision = searchParams.get('revision') || '';
  const [availableRevisions, setAvailableRevisions] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jsonBusy, setJsonBusy] = useState(false);
  const loadSequence = useRef(0);
  const { getToken } = useAuth(); const { user } = useUser();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canManage = ['admin','quality-manager','production-engineer','service'].includes(role);
  const supplierKey = ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(String(role||'').replace(/^test-/,''));

  const [item, setItem] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [error, setError] = useState('');
  const [specInput, setSpecInput] = useState({ name:'',value:'',unit:'' });
  const [usedInFlows, setUsedInFlows] = useState([]);
  const [showCalModal, setShowCalModal] = useState(false);
  const [calForm, setCalForm] = useState({ date:'', notes:'', passed:true, certificate:'',
    calibratedByOrg:'', referenceStandardUsed:'', traceableTo:'',
    asFound:'', asLeft:'', outOfTolerance:false, impactAssessment:'', customerNotified:false });
  const [calSaving, setCalSaving] = useState(false);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true); setError(''); setEditing(false);
    try {
      const token = await getToken();
      const query = requestedRevision ? `?revision=${encodeURIComponent(requestedRevision)}` : '';
      const res = await window.fetch(`${API_BASE}/quality/equipment/${id}${query}`, { headers:{ Authorization:`Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || 'Could not load document');
      if (sequence === loadSequence.current) {
        setItem(data.item); setForm(JSON.parse(JSON.stringify(data.item)));
        setAvailableRevisions(data.availableRevisions || []); setSnapshot(data.snapshot || null);
      }
    } catch (err) { if (sequence === loadSequence.current) { setError(err.message); setItem(null); } }
    finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [id, getToken, requestedRevision]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!id) return;
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/process-flows/used-in?resource=equipment&id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => { if (d.ok) setUsedInFlows(d.flows || []); }).catch(() => {})
    );
  }, [id, getToken]);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleSupplier = s => {
    const cur = form.visibility?.suppliers||[];
    const next = cur.includes(s)?cur.filter(x=>x!==s):[...cur,s];
    setForm(f=>({...f,visibility:{...f.visibility,suppliers:next,supplierAccess:next.length?'Specific':'None'}}));
  };
  const addSpec = () => {
    if (!specInput.name.trim()) return;
    setForm(f=>({...f,specifications:[...(f.specifications||[]),{...specInput}]}));
    setSpecInput({ name:'',value:'',unit:'' });
  };
  const removeSpec = i => setForm(f=>({...f,specifications:f.specifications.filter((_,j)=>j!==i)}));

  const handleSave = async () => {
    if (requestedRevision || snapshot || item?.currentStatus !== 'Draft') return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/equipment/${id}`, {
        method:'PUT', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ ...resourceEditContent(form), changeSummary, expectedVersion:item.__v }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Save failed');
      setItem(data.item); setForm(JSON.parse(JSON.stringify(data.item)));
      setEditing(false); setShowSaveModal(false); setChangeSummary('');
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleAction = async action => {
    if (requestedRevision || snapshot || jsonBusy) return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/equipment/${id}/submit`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ action, changeSummary: actionNote, expectedVersion:item.__v }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Action failed');
      setItem(data.item); setForm(JSON.parse(JSON.stringify(data.item)));
      setActionModal(null); setActionNote('');
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const availableActions = item && !requestedRevision && !snapshot ? Object.entries(TRANSITIONS)
    .filter(([,t]) => !t.from || t.from === item.currentStatus)
    .map(([key,t]) => ({ key, label:t.label }))
    .filter(a => canManage
      || (supplierKey && a.key === 'submit' && item?.createdBy === user?.id && item?.currentStatus !== 'Released')
      || (supplierKey && a.key === 'revise' && item?.currentStatus === 'Released')) : [];
  const canEditThis = !requestedRevision && !snapshot && item?.currentStatus === 'Draft'
    && (canManage || (supplierKey && (item?.createdBy === user?.id
      || ((item?.visibility?.suppliers || []).includes(String(role).replace(/^test-/,''))
        && (item?.revisions || []).some(r => r.status === 'Released')))));
  const unresolvedBlockers = (item?.releaseBlockers || []).some(b => !b.resolved);
  const canLogEvents = !requestedRevision && !snapshot && !['Obsolete','Superseded'].includes(item?.currentStatus)
    && (canManage || (supplierKey && (item?.createdBy === user?.id || (item?.visibility?.suppliers || []).includes(String(role).replace(/^test-/,'')))));

  const logCalibration = async () => {
    if (requestedRevision || snapshot) return;
    setCalSaving(true);
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/equipment/${id}/calibrate`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ ...calForm, expectedVersion:item.__v }),
      });
      const data = await res.json();
      if (data.ok) { setItem(data.item); setForm(JSON.parse(JSON.stringify(data.item))); setShowCalModal(false); setCalForm({ date:'', notes:'', passed:true, certificate:'', calibratedByOrg:'', referenceStandardUsed:'', traceableTo:'', asFound:'', asLeft:'', outOfTolerance:false, impactAssessment:'', customerNotified:false }); }
    } catch {} finally { setCalSaving(false); }
  };

  const calStatus = (() => {
    if (!item?.calibrationRequired) return null;
    if (!item.lastCalibratedAt) return 'never';
    if (item.nextCalibrationDue && new Date(item.nextCalibrationDue) < new Date()) return 'overdue';
    if (item.nextCalibrationDue && new Date(item.nextCalibrationDue) < new Date(Date.now() + 30*86400000)) return 'due-soon';
    return 'ok';
  })();
  const calStatusColor = { never:'#94a3b8', overdue:'#EC2124', 'due-soon':'#f59e0b', ok:'#46BB89' };
  const calStatusLabel = { never:'Never Calibrated', overdue:'Overdue', 'due-soon':'Due Soon', ok:'Up to Date' };

  if (loading) return <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>Loading…</div>;

  if (!item) return <div role="alert">{error || 'Document unavailable'} <button onClick={load}>Retry</button></div>;

  const inp = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'8px 12px',fontSize:13,width:'100%',boxSizing:'border-box' };
  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };
  const readVal = v => <span style={{ fontSize:14 }}>{v||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>;

  return (
    <div style={{ maxWidth:780 }}>
      {/* Header */}
      <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:6 }}>
        <button onClick={()=>navigate('/quality/equipment')}
          style={{ background:'none',border:'none',color:'var(--ni-text-muted)',cursor:'pointer',fontSize:20,padding:0 }}>←</button>
        <div>
          <div style={{ fontSize:11,fontFamily:'monospace',color:'#f59e0b',marginBottom:2 }}>{item.documentNumber}</div>
          <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>{item.name}</h2>
        </div>
        <div style={{ marginLeft:'auto',display:'flex',gap:8,alignItems:'center' }}>
          <StatusBadge status={item.currentStatus} />
          <span style={{ fontSize:12,color:'var(--ni-text-muted)',fontFamily:'monospace' }}>Rev. {item.currentRevision}</span>
        </div>
      </div>

      {error && <div style={{ color:'#EC2124',fontSize:13,margin:'12px 0',padding:'8px 12px',background:'#EC212411',borderRadius:6 }}>{error}</div>}

      <ProcedureRevisionBar entry={item} requestedRevision={requestedRevision} revisions={availableRevisions} snapshot={snapshot}
        revisionLocked={editing || saving || jsonBusy || showCalModal || calSaving}
        onRevisionChange={revision => { if (!(editing || saving || jsonBusy || showCalModal || calSaving)) setSearchParams(revision ? { revision } : {}); }} allowAuthorDownload={false} />
      <ControlledDraftJson library="equipment" record={item} revision={requestedRevision || snapshot?.revision || ''}
        blocked={editing || saving || showCalModal || calSaving} onBusyChange={setJsonBusy} onApplied={load} />
      <ProcedureAuthoringMetadata value={editing ? form : item} editing={editing} onChange={set} staff={canManage} />

      {/* Action bar */}
      {(canEditThis || availableActions.length > 0) && (
        <div style={{ display:'flex',gap:8,marginBottom:20,flexWrap:'wrap' }}>
          {!editing ? (
            <>
              {canEditThis && <button onClick={()=>setEditing(true)} disabled={!canEditThis || jsonBusy}
                style={{ background:'#f59e0b22',color:'#f59e0b',border:'1px solid #f59e0b44',borderRadius:6,
                  padding:'6px 16px',fontSize:12,fontWeight:700,cursor:'pointer' }}>Edit</button>}
              {availableActions.map(a=>(
                <button key={a.key} disabled={saving || jsonBusy || (a.key === 'approve' && unresolvedBlockers)} onClick={()=>{setActionModal(a.key);setActionNote('');}}
                  style={{ background:'rgba(255,255,255,0.05)',border:'1px solid var(--ni-border-soft)',
                    color:'var(--ni-text)',borderRadius:6,padding:'6px 16px',fontSize:12,fontWeight:600,cursor:'pointer' }}>
                  {a.label}
                </button>
              ))}
            </>
          ) : (
            <>
              <button onClick={()=>setShowSaveModal(true)}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,
                  padding:'6px 16px',fontSize:12,fontWeight:700,cursor:'pointer' }}>Save Changes</button>
              <button onClick={()=>{ setEditing(false); setForm(JSON.parse(JSON.stringify(item))); }}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
                  borderRadius:6,padding:'6px 16px',fontSize:12,cursor:'pointer' }}>Cancel</button>
            </>
          )}
        </div>
      )}

      {/* Identity */}
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:16 }}>
        {[['Type', 'type', EQUIPMENT_TYPES],['Manufacturer','manufacturer',null],['Model','model',null]].map(([lab,key,opts])=>(
          <div key={key} style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:14 }}>
            <div style={labelS}>{lab}</div>
            {editing ? (opts
              ? <select value={form[key]||''} onChange={e=>set(key,e.target.value)} style={inp}>
                  {opts.map(t=><option key={t}>{t}</option>)}</select>
              : <input value={form[key]||''} onChange={e=>set(key,e.target.value)} style={inp} />)
            : readVal(item[key])}
          </div>
        ))}
      </div>

      {/* Description + notes */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ marginBottom:12 }}>
          <div style={labelS}>Description</div>
          {editing ? <textarea value={form.description||''} onChange={e=>set('description',e.target.value)}
            rows={3} style={{ ...inp,resize:'vertical' }} />
          : <p style={{ margin:0,fontSize:14,lineHeight:1.6 }}>{item.description||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</p>}
        </div>
        <div>
          <div style={labelS}>Notes</div>
          {editing ? <textarea value={form.notes||''} onChange={e=>set('notes',e.target.value)}
            rows={2} style={{ ...inp,resize:'vertical' }} />
          : <p style={{ margin:0,fontSize:14,lineHeight:1.6 }}>{item.notes||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</p>}
        </div>
      </div>

      {/* Asset & identity (ISO/IEC 17025 §6.4 — unique unit, location, custody) */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ ...labelS, marginBottom:12 }}>Asset &amp; Identity</div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12 }}>
          {[['assetId','Asset ID / Tag'],['serialNumber','Serial Number'],['location','Location'],
            ['ownerOrg','Owner Org'],['responsiblePerson','Responsible Person'],
            ['firmwareVersion','Firmware Version'],['softwareVersion','Software Version']].map(([k,lbl])=>(
            <div key={k}><div style={labelS}>{lbl}</div>
              {editing ? <input value={form[k]||''} onChange={e=>set(k,e.target.value)} style={inp} /> : readVal(item[k])}</div>
          ))}
          <div><div style={labelS}>In-Service Date</div>
            {editing ? <input aria-label="Operational in-service date" type="date" readOnly value={item.inServiceDate?String(item.inServiceDate).slice(0,10):''} style={inp} />
            : <span style={{ fontSize:14 }}>{item.inServiceDate?new Date(item.inServiceDate).toLocaleDateString():<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>}</div>
          <div><div style={labelS}>Decommissioned</div>
            {editing ? <input aria-label="Operational decommissioned date" type="date" readOnly value={item.decommissionedAt?String(item.decommissionedAt).slice(0,10):''} style={inp} />
            : <span style={{ fontSize:14 }}>{item.decommissionedAt?new Date(item.decommissionedAt).toLocaleDateString():<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>}</div>
        </div>
      </div>

      {/* Calibration */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
          <div style={labelS}>Calibration</div>
          {!editing && item.calibrationRequired && canLogEvents && (
            <button onClick={()=>setShowCalModal(true)}
              style={{ background:'#f59e0b22',color:'#f59e0b',border:'1px solid #f59e0b44',borderRadius:6,
                padding:'4px 12px',fontSize:12,fontWeight:700,cursor:'pointer' }}>
              + Log Calibration
            </button>
          )}
        </div>
        {editing ? (
          <>
            <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:10 }}>
              <input type="checkbox" id="calreq2" checked={!!form.calibrationRequired}
                onChange={e=>set('calibrationRequired',e.target.checked)} />
              <label htmlFor="calreq2" style={{ fontSize:13,cursor:'pointer' }}>Calibration Required</label>
            </div>
            {form.calibrationRequired && (
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12 }}>
                <div><div style={labelS}>Interval (days)</div>
                  <input type="number" value={form.calibrationIntervalDays||''} onChange={e=>set('calibrationIntervalDays',e.target.value)} style={inp} /></div>
                <div><div style={labelS}>Standard</div>
                  <input value={form.calibrationStandard||''} onChange={e=>set('calibrationStandard',e.target.value)} style={inp} /></div>
                <div><div style={labelS}>Status</div>
                  <span style={{ fontSize:13 }}>{item.calibrationStatus || 'Not Required'}</span></div>
              </div>
            )}
          </>
        ) : item.calibrationRequired ? (
          <>
            <div style={{ display:'flex',gap:16,flexWrap:'wrap',marginBottom:8 }}>
              <div style={{ fontSize:13,color:'var(--ni-text-muted)' }}>
                Every {item.calibrationIntervalDays||'?'} days{item.calibrationStandard?` · ${item.calibrationStandard}`:''}
              </div>
              {calStatus && (
                <span style={{ fontSize:11,fontWeight:700,color:calStatusColor[calStatus],
                  background:calStatusColor[calStatus]+'22',borderRadius:4,padding:'2px 8px',letterSpacing:'0.04em' }}>
                  {calStatusLabel[calStatus]}
                </span>
              )}
              {(item.calibrationStatus==='Out of Service'||item.calibrationStatus==='Out of Calibration') && (
                <span style={{ fontSize:11,fontWeight:700,color:'#EC2124',background:'#EC212422',borderRadius:4,padding:'2px 8px',letterSpacing:'0.04em' }}>
                  {item.calibrationStatus.toUpperCase()}
                </span>
              )}
            </div>
            <div style={{ display:'flex',gap:24,fontSize:12,color:'var(--ni-text-muted)' }}>
              <span>Last: {item.lastCalibratedAt ? new Date(item.lastCalibratedAt).toLocaleDateString() : '—'}</span>
              <span>Next due: {item.nextCalibrationDue ? new Date(item.nextCalibrationDue).toLocaleDateString() : '—'}</span>
            </div>
          </>
        ) : (
          <div style={{ fontSize:14,color:'var(--ni-text-muted)' }}>Not required</div>
        )}

        {/* Calibration history */}
        {!editing && item.calibrationRequired && (item.calibrationHistory||[]).length > 0 && (
          <div style={{ marginTop:14 }}>
            <div style={{ fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',
              color:'var(--ni-text-muted)',marginBottom:6 }}>History</div>
            <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
              <thead>
                <tr style={{ color:'var(--ni-text-muted)',fontSize:10,textTransform:'uppercase',letterSpacing:'0.06em' }}>
                  {['Date','By','Result','Certificate','Notes'].map(h=>(
                    <th key={h} style={{ padding:'4px 8px',textAlign:'left',fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...(item.calibrationHistory||[])].reverse().map((e,i)=>(
                  <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                    <td style={{ padding:'7px 8px',whiteSpace:'nowrap' }}>{new Date(e.date).toLocaleDateString()}</td>
                    <td style={{ padding:'7px 8px',color:'var(--ni-text-muted)' }}>{e.performedByName||'—'}</td>
                    <td style={{ padding:'7px 8px' }}>
                      <span style={{ fontSize:10,fontWeight:700,color:e.passed?'#46BB89':'#EC2124',
                        background:e.passed?'#46BB8920':'#EC212420',borderRadius:3,padding:'1px 6px' }}>
                        {e.passed?'PASS':'FAIL'}
                      </span>
                    </td>
                    <td style={{ padding:'7px 8px',color:'var(--ni-text-muted)',fontFamily:'monospace',fontSize:11 }}>{e.certificate||'—'}</td>
                    <td style={{ padding:'7px 8px',color:'var(--ni-text-muted)' }}>{e.notes||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Specifications */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Specifications</div>
        {((editing?form:item).specifications||[]).length===0 && !editing
          ? <div style={{ fontSize:14,color:'var(--ni-text-muted)',marginTop:6 }}>—</div>
          : null}
        {((editing?form:item).specifications||[]).map((s,i)=>(
          <div key={i} style={{ display:'flex',gap:8,alignItems:'center',padding:'6px 0',borderBottom:'1px solid var(--ni-border-soft)' }}>
            <span style={{ flex:1,fontSize:13,fontWeight:600 }}>{s.name}</span>
            <span style={{ fontSize:13,color:'var(--ni-text-muted)' }}>{s.value}{s.unit?` ${s.unit}`:''}</span>
            {editing && <button type="button" onClick={()=>removeSpec(i)}
              style={{ background:'none',border:'none',color:'#EC2124',cursor:'pointer',fontSize:16 }}>×</button>}
          </div>
        ))}
        {editing && (
          <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 80px 36px',gap:8,marginTop:10 }}>
            <input placeholder="Parameter" value={specInput.name} onChange={e=>setSpecInput(s=>({...s,name:e.target.value}))} style={inp} />
            <input placeholder="Value" value={specInput.value} onChange={e=>setSpecInput(s=>({...s,value:e.target.value}))} style={inp} />
            <input placeholder="Unit" value={specInput.unit} onChange={e=>setSpecInput(s=>({...s,unit:e.target.value}))} style={inp} />
            <button type="button" onClick={addSpec}
              style={{ background:'#f59e0b22',color:'#f59e0b',border:'1px solid #f59e0b44',borderRadius:6,fontSize:18,cursor:'pointer',fontWeight:700,padding:0 }}>+</button>
          </div>
        )}
      </div>

      {/* Key metric — auto-fills when this item is linked as a tool on a step */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Key Metric</div>
        <div style={{ fontSize:12,color:'var(--ni-text-muted)',margin:'4px 0 10px' }}>The default spec auto-filled when this item is linked as a tool on an assembly step.</div>
        {editing
          ? <MetricInput value={form.keyMetric||{}} onChange={v=>set('keyMetric',v)} getToken={getToken} accent="#f59e0b" />
          : (item.keyMetric && item.keyMetric.metricType
              ? <span style={{ fontSize:14 }}>{item.keyMetric.metricType}: <strong>{item.keyMetric.metricValue} {item.keyMetric.metricUnit}</strong></span>
              : <span style={{ fontSize:14,color:'var(--ni-text-muted)' }}>—</span>)}
      </div>

      {/* Measurement Systems Analysis (IATF 16949 §7.1.5.1.1) */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10 }}>
          <div style={labelS}>Measurement Systems Analysis (MSA)</div>
          {editing && (
            <label style={{ display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer' }}>
              <input type="checkbox" checked={!!form.msaRequired} onChange={e=>set('msaRequired',e.target.checked)} /> MSA required
            </label>
          )}
        </div>
        {!editing && item.msaRequired && <div style={{ fontSize:12,color:'#f59e0b',marginBottom:8 }}>MSA required for this gauge</div>}
        {(item.msaHistory||[]).length === 0 ? (
          <div style={{ fontSize:13,color:'var(--ni-text-muted)' }}>No MSA studies recorded.</div>
        ) : (item.msaHistory||[]).map((m,i)=>(
          <div key={i} style={{ display:'flex',gap:10,alignItems:'center',padding:'6px 0',borderBottom:'1px solid var(--ni-border-soft)',fontSize:13,flexWrap:'wrap' }}>
            <span style={{ fontWeight:600 }}>{m.studyType}</span>
            {m.percentRR!=null && m.percentRR!=='' && <span style={{ color:'var(--ni-text-muted)' }}>%R&R: {m.percentRR}</span>}
            {m.result && <span style={{ color:'var(--ni-text-muted)' }}>{m.result}</span>}
            <span style={{ marginLeft:'auto',fontSize:10,fontWeight:700,color:m.acceptable?'#46BB89':'#EC2124',background:(m.acceptable?'#46BB89':'#EC2124')+'22',borderRadius:4,padding:'1px 6px' }}>{m.acceptable?'ACCEPTABLE':'NOT ACCEPTABLE'}</span>
          </div>
        ))}
        <p style={{ fontSize:12, color:'var(--ni-text-muted)' }}>MSA results are operational history and cannot be changed through a document edit.</p>
      </div>

      {/* Supplier visibility */}
      {canManage && (
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
          <div style={labelS}>Supplier Visibility</div>
          <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginTop:4 }}>
            {SUPPLIERS.map(s=>(
              editing ? (
                <button type="button" key={s} onClick={()=>toggleSupplier(s)}
                  style={{ padding:'5px 14px',borderRadius:20,fontSize:12,fontWeight:700,cursor:'pointer',border:'1px solid',
                    background:(form.visibility?.suppliers||[]).includes(s)?'#f59e0b22':'transparent',
                    color:(form.visibility?.suppliers||[]).includes(s)?'#f59e0b':'var(--ni-text-muted)',
                    borderColor:(form.visibility?.suppliers||[]).includes(s)?'#f59e0b':'var(--ni-border-soft)' }}>
                  {s.toUpperCase()}
                </button>
              ) : (
                <span key={s} style={{ padding:'3px 12px',borderRadius:20,fontSize:11,fontWeight:700,
                  background:(item.visibility?.suppliers||[]).includes(s)?'#f59e0b22':'rgba(255,255,255,0.04)',
                  color:(item.visibility?.suppliers||[]).includes(s)?'#f59e0b':'var(--ni-text-muted)' }}>
                  {s.toUpperCase()}
                </span>
              )
            ))}
          </div>
        </div>
      )}

      {/* Used In Flows */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Used In Process Flows</div>
        {usedInFlows.length===0
          ? <div style={{ fontSize:13,color:'var(--ni-text-muted)',marginTop:4 }}>Not linked to any flow yet.</div>
          : usedInFlows.map(f=>(
            <div key={f._id} onClick={()=>navigate(`/quality/process/${f._id}`)}
              style={{ display:'flex',gap:10,alignItems:'center',padding:'7px 0',
                borderBottom:'1px solid var(--ni-border-soft)',cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.opacity='0.75'}
              onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
              <span style={{ fontFamily:'monospace',fontSize:11,color:'#60a5fa',flexShrink:0 }}>{f.documentNumber}</span>
              <span style={{ fontSize:13,flex:1 }}>{f.name}</span>
              <StatusBadge status={f.currentStatus}/>
            </div>
          ))
        }
      </div>

      {/* Source of record */}
      {item.source?.url && (
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
          <div style={labelS}>Source of record</div>
          <a href={item.source.url} target="_blank" rel="noopener noreferrer"
            style={{ fontSize:13,color:'#f59e0b',textDecoration:'none',wordBreak:'break-word' }}>
            {(item.source.system||'SharePoint')}{item.source.fileName?` — ${item.source.fileName}`:''} ↗
          </a>
          {item.source.externalModifiedAt && <div style={{ fontSize:11,color:'var(--ni-text-muted)',marginTop:4 }}>Updated {new Date(item.source.externalModifiedAt).toLocaleDateString()}</div>}
        </div>
      )}

      {/* Revision history */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16 }}>
        <div style={labelS}>Revision History</div>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12,marginTop:8 }}>
          <thead>
            <tr style={{ color:'var(--ni-text-muted)',fontSize:10,textTransform:'uppercase',letterSpacing:'0.06em' }}>
              {['Rev','Status','Summary','By','Date'].map(h=>(
                <th key={h} style={{ padding:'6px 10px',textAlign:'left',fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...(item.revisions||[])].reverse().map((r,i)=>(
              <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                <td style={{ padding:'8px 10px',fontFamily:'monospace' }}>{r.revision}</td>
                <td style={{ padding:'8px 10px' }}><StatusBadge status={r.status}/></td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>{r.changeSummary}</td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>{r.updatedByName}</td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>
                  {r.updatedAt?new Date(r.updatedAt).toLocaleDateString():'—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save modal */}
      {showSaveModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>Save Changes</h3>
            <div style={labelS}>Change Summary</div>
            <input value={changeSummary} onChange={e=>setChangeSummary(e.target.value)}
              placeholder="Describe what changed…" style={{ ...inp,marginBottom:20 }} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={handleSave} disabled={saving}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer' }}>
                {saving?'Saving…':'Save'}
              </button>
              <button onClick={()=>setShowSaveModal(false)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>{TRANSITIONS[actionModal]?.label}</h3>
            <div style={labelS}>Note (optional)</div>
            <input value={actionNote} onChange={e=>setActionNote(e.target.value)}
              placeholder="Reason or comment…" style={{ ...inp,marginBottom:20 }} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={()=>handleAction(actionModal)} disabled={saving}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer' }}>
                {saving?'Processing…':'Confirm'}
              </button>
              <button onClick={()=>setActionModal(null)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Calibration log modal */}
      {showCalModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:440 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>Log Calibration</h3>
            <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
              <div>
                <div style={labelS}>Date *</div>
                <input type="date" value={calForm.date} onChange={e=>setCalForm(f=>({...f,date:e.target.value}))} style={inp} />
              </div>
              <div>
                <div style={labelS}>Certificate / Reference</div>
                <input value={calForm.certificate} onChange={e=>setCalForm(f=>({...f,certificate:e.target.value}))}
                  style={inp} placeholder="e.g. CAL-2024-0042" />
              </div>
              {/* Metrological traceability (ISO/IEC 17025 §6.5) */}
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10 }}>
                <div><div style={labelS}>Calibrated by (org)</div>
                  <input value={calForm.calibratedByOrg} onChange={e=>setCalForm(f=>({...f,calibratedByOrg:e.target.value}))} style={inp} placeholder="e.g. DNV cal lab" /></div>
                <div><div style={labelS}>Traceable to</div>
                  <input value={calForm.traceableTo} onChange={e=>setCalForm(f=>({...f,traceableTo:e.target.value}))} style={inp} placeholder="e.g. NIST / national std" /></div>
              </div>
              <div>
                <div style={labelS}>Reference standard used</div>
                <input value={calForm.referenceStandardUsed} onChange={e=>setCalForm(f=>({...f,referenceStandardUsed:e.target.value}))} style={inp} placeholder="e.g. Fluke 5522A" />
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10 }}>
                <div><div style={labelS}>As found</div>
                  <input value={calForm.asFound} onChange={e=>setCalForm(f=>({...f,asFound:e.target.value}))} style={inp} placeholder="reading before adj." /></div>
                <div><div style={labelS}>As left</div>
                  <input value={calForm.asLeft} onChange={e=>setCalForm(f=>({...f,asLeft:e.target.value}))} style={inp} placeholder="reading after adj." /></div>
              </div>
              <div>
                <div style={labelS}>Notes</div>
                <textarea value={calForm.notes} onChange={e=>setCalForm(f=>({...f,notes:e.target.value}))}
                  rows={2} style={{ ...inp,resize:'vertical' }} placeholder="Observations, conditions…" />
              </div>
              <div style={{ display:'flex',gap:18,flexWrap:'wrap' }}>
                <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer' }}>
                  <input type="checkbox" checked={calForm.passed} onChange={e=>setCalForm(f=>({...f,passed:e.target.checked}))} style={{ accentColor:'#46BB89' }} />
                  Calibration passed
                </label>
                <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer' }}>
                  <input type="checkbox" checked={calForm.outOfTolerance} onChange={e=>setCalForm(f=>({...f,outOfTolerance:e.target.checked}))} style={{ accentColor:'#EC2124' }} />
                  Found out of tolerance
                </label>
              </div>
              {calForm.outOfTolerance && (
                <>
                  <div>
                    <div style={labelS}>Impact assessment (prior product)</div>
                    <textarea value={calForm.impactAssessment} onChange={e=>setCalForm(f=>({...f,impactAssessment:e.target.value}))}
                      rows={2} style={{ ...inp,resize:'vertical' }} placeholder="Risk to product measured since last good cal…" />
                  </div>
                  <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer' }}>
                    <input type="checkbox" checked={calForm.customerNotified} onChange={e=>setCalForm(f=>({...f,customerNotified:e.target.checked}))} />
                    Customer notified
                  </label>
                </>
              )}
              <div style={{ display:'flex',gap:10 }}>
                <button onClick={logCalibration} disabled={calSaving||!calForm.date}
                  style={{ background:'#f59e0b',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer' }}>
                  {calSaving?'Saving…':'Save Log'}
                </button>
                <button onClick={()=>setShowCalModal(false)}
                  style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
